import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Win98Dialog from './Win98Dialog';
import './AdminPanel.css';
import api from '../utils/api';

const AdminPanel = ({ user }) => {
  const location = useLocation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [creditAmount, setCreditAmount] = useState(2);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [adminPassword, setAdminPassword] = useState('');
  const [adminPasswordEntered, setAdminPasswordEntered] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [activeTab, setActiveTab] = useState('users'); // 'users' or 'messages'
  const [messages, setMessages] = useState([]);
  const [messagesPage, setMessagesPage] = useState(1);
  const [messagesTotal, setMessagesTotal] = useState(0);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [showWarnModal, setShowWarnModal] = useState(null);
  const [showBanModal, setShowBanModal] = useState(null);
  const [warnReason, setWarnReason] = useState('');
  const [banReason, setBanReason] = useState('');
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [payoutsLoading, setPayoutsLoading] = useState(false);
  const [verificationCodes, setVerificationCodes] = useState({}); // batchId -> code
  const [verifying, setVerifying] = useState({}); // batchId -> boolean
  const [syncing, setSyncing] = useState({}); // batchId -> boolean

  useEffect(() => {
    // Try to load users - if ADMIN_PASSWORD is not set, this will work
    // If it is set, we'll get a 403 and show the password prompt
    // Note: AdminRoute already verified we're admin, so this is just for password check
    const checkAdminAccess = async () => {
      try {
        await api.get('/api/admin/users?page=1&limit=1');
        // If successful, no password required
        setAdminPasswordEntered(true);
      } catch (error) {
        if (error.response?.status === 403 && error.response?.data?.error === 'Admin password required') {
          // Password required - this is expected, don't log as error
          setAdminPasswordEntered(false);
        } else {
          // Other error - shouldn't happen since AdminRoute already checked, but handle it
          console.warn('Admin access check failed:', error.response?.status || error.message);
          setAdminPasswordEntered(false);
        }
      }
    };
    checkAdminAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (adminPasswordEntered) {
      loadUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchTerm, adminPasswordEntered]);

  const handleAdminPasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    
    // Store password for API requests
    // We'll add it to localStorage temporarily (not ideal but works)
    localStorage.setItem('admin_password', adminPassword);
    
    // Try to load users to verify password
    try {
      setLoading(true);
      await api.get('/api/admin/users?page=1&limit=1', {
        headers: {
          'X-Admin-Password': adminPassword
        }
      });
      setAdminPasswordEntered(true);
      setLoading(false);
    } catch (error) {
      setPasswordError(error.response?.data?.error || 'Invalid admin password');
      localStorage.removeItem('admin_password');
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50'
      });
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      const storedPassword = localStorage.getItem('admin_password');
      const headers = storedPassword ? { 'X-Admin-Password': storedPassword } : {};
      
      const response = await api.get(`/api/admin/users?${params}`, { headers });
      setUsers(response.data.users);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error loading users:', error);
      if (error.response?.status === 403) {
        setAdminPasswordEntered(false);
        localStorage.removeItem('admin_password');
        setPasswordError('Admin password required or invalid');
      }
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to load users'
      });
    } finally {
      setLoading(false);
    }
  };

  const getAdminHeaders = () => {
    const adminPassword = localStorage.getItem('admin_password');
    return adminPassword ? { 'X-Admin-Password': adminPassword } : {};
  };

  const handleAddCredits = async (userId, amount) => {
    try {
      const response = await api.post(
        `/api/admin/users/${userId}/credits`,
        { amount: parseFloat(amount) },
        { headers: getAdminHeaders() }
      );
      setMessage({
        type: 'success',
        text: response.data.message
      });
      loadUsers();
      if (selectedUser?.id === userId) {
        loadUserDetails(userId);
      }
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      if (error.response?.status === 403) {
        setAdminPasswordEntered(false);
        localStorage.removeItem('admin_password');
        setPasswordError('Admin password required or invalid');
      }
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to add credits'
      });
    }
  };

  const handleBulkAddCredits = async () => {
    if (selectedUserIds.length === 0) {
      setMessage({
        type: 'error',
        text: 'Please select at least one user'
      });
      return;
    }

    try {
      const response = await api.post(
        '/api/admin/users/bulk-credits',
        {
          userIds: selectedUserIds,
          amount: parseFloat(creditAmount)
        },
        { headers: getAdminHeaders() }
      );
      setMessage({
        type: 'success',
        text: response.data.message
      });
      setSelectedUserIds([]);
      loadUsers();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      if (error.response?.status === 403) {
        setAdminPasswordEntered(false);
        localStorage.removeItem('admin_password');
        setPasswordError('Admin password required or invalid');
      }
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to add credits'
      });
    }
  };

  const loadUserDetails = async (userId) => {
    try {
      const response = await api.get(`/api/admin/users/${userId}`, {
        headers: getAdminHeaders()
      });
      setSelectedUser(response.data);
    } catch (error) {
      console.error('Error loading user details:', error);
      if (error.response?.status === 403) {
        setAdminPasswordEntered(false);
        localStorage.removeItem('admin_password');
        setPasswordError('Admin password required or invalid');
      }
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const selectAll = () => {
    if (selectedUserIds.length === users.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(users.map(u => u.id));
    }
  };

  const loadMessages = async () => {
    try {
      setMessagesLoading(true);
      const params = new URLSearchParams({
        page: messagesPage.toString(),
        limit: '50'
      });
      const response = await api.get(`/api/admin/messages?${params}`, {
        headers: getAdminHeaders()
      });
      setMessages(response.data.messages);
      setMessagesTotal(response.data.total);
    } catch (error) {
      console.error('Error loading messages:', error);
      if (error.response?.status === 403) {
        setAdminPasswordEntered(false);
        localStorage.removeItem('admin_password');
        setPasswordError('Admin password required or invalid');
      }
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to load messages'
      });
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleDeleteMessage = async (messageId, reason = 'Deleted by admin') => {
    if (!window.confirm('Are you sure you want to delete this message?')) {
      return;
    }

    try {
      await api.delete(`/api/admin/messages/${messageId}`, {
        data: { reason },
        headers: getAdminHeaders()
      });
      setMessage({
        type: 'success',
        text: 'Message deleted successfully'
      });
      loadMessages();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      if (error.response?.status === 403) {
        setAdminPasswordEntered(false);
        localStorage.removeItem('admin_password');
        setPasswordError('Admin password required or invalid');
      }
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to delete message'
      });
    }
  };

  const loadPendingPayouts = async () => {
    try {
      setPayoutsLoading(true);
      const response = await api.get('/api/admin/payouts/pending', {
        headers: getAdminHeaders()
      });
      setPendingPayouts(response.data.pendingPayouts || []);
    } catch (error) {
      console.error('Error loading pending payouts:', error);
      if (error.response?.status === 403) {
        setAdminPasswordEntered(false);
        localStorage.removeItem('admin_password');
        setPasswordError('Admin password required or invalid');
      }
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to load pending payouts'
      });
    } finally {
      setPayoutsLoading(false);
    }
  };

  const handleVerifyPayout = async (batchId) => {
    const verificationCode = verificationCodes[batchId];
    if (!verificationCode || !verificationCode.trim()) {
      setMessage({
        type: 'error',
        text: 'Please enter a verification code'
      });
      return;
    }

    try {
      setVerifying(prev => ({ ...prev, [batchId]: true }));
      await api.post(
        '/api/admin/payouts/verify',
        { batchId, verificationCode },
        { headers: getAdminHeaders() }
      );
      setMessage({
        type: 'success',
        text: 'Payout verified successfully!'
      });
      // Clear the verification code
      setVerificationCodes(prev => {
        const newCodes = { ...prev };
        delete newCodes[batchId];
        return newCodes;
      });
      // Reload pending payouts
      loadPendingPayouts();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      if (error.response?.status === 403) {
        setAdminPasswordEntered(false);
        localStorage.removeItem('admin_password');
        setPasswordError('Admin password required or invalid');
      }
      setMessage({
        type: 'error',
        text: error.response?.data?.error || error.response?.data?.details || 'Failed to verify payout'
      });
    } finally {
      setVerifying(prev => ({ ...prev, [batchId]: false }));
    }
  };

  const handleSyncStatus = async (batchId) => {
    try {
      setSyncing(prev => ({ ...prev, [batchId]: true }));
      const response = await api.post(
        '/api/admin/payouts/sync-status',
        { batchId },
        { headers: getAdminHeaders() }
      );
      setMessage({
        type: 'success',
        text: response.data.message || 'Payout status synced successfully!'
      });
      // Reload pending payouts to reflect updated status
      loadPendingPayouts();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      if (error.response?.status === 403) {
        setAdminPasswordEntered(false);
        localStorage.removeItem('admin_password');
        setPasswordError('Admin password required or invalid');
      }
      setMessage({
        type: 'error',
        text: error.response?.data?.error || error.response?.data?.details || 'Failed to sync payout status'
      });
    } finally {
      setSyncing(prev => ({ ...prev, [batchId]: false }));
    }
  };

  const handleWarn = async () => {
    if (!warnReason.trim()) {
      setMessage({
        type: 'error',
        text: 'Please provide a reason for the warning'
      });
      return;
    }

    try {
      const response = await api.post(
        `/api/admin/users/${showWarnModal.id}/warn`,
        { reason: warnReason },
        { headers: getAdminHeaders() }
      );
      setMessage({
        type: 'success',
        text: response.data.message
      });
      setShowWarnModal(null);
      setWarnReason('');
      loadUsers();
      if (selectedUser?.user.id === showWarnModal.id) {
        loadUserDetails(showWarnModal.id);
      }
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      if (error.response?.status === 403) {
        setAdminPasswordEntered(false);
        localStorage.removeItem('admin_password');
        setPasswordError('Admin password required or invalid');
      }
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to issue warning'
      });
    }
  };

  const handleBan = async () => {
    if (!banReason.trim()) {
      setMessage({
        type: 'error',
        text: 'Please provide a reason for the ban'
      });
      return;
    }

    if (!window.confirm(`Are you sure you want to ban ${showBanModal.username}?`)) {
      return;
    }

    try {
      const response = await api.post(
        `/api/admin/users/${showBanModal.id}/ban`,
        { reason: banReason },
        { headers: getAdminHeaders() }
      );
      setMessage({
        type: 'success',
        text: response.data.message
      });
      setShowBanModal(null);
      setBanReason('');
      loadUsers();
      if (selectedUser?.user.id === showBanModal.id) {
        loadUserDetails(showBanModal.id);
      }
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      if (error.response?.status === 403) {
        setAdminPasswordEntered(false);
        localStorage.removeItem('admin_password');
        setPasswordError('Admin password required or invalid');
      }
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to ban user'
      });
    }
  };

  const handleUnban = async (userId) => {
    if (!window.confirm('Are you sure you want to unban this user?')) {
      return;
    }

    try {
      const response = await api.post(
        `/api/admin/users/${userId}/unban`,
        {},
        { headers: getAdminHeaders() }
      );
      setMessage({
        type: 'success',
        text: response.data.message
      });
      loadUsers();
      if (selectedUser?.user.id === userId) {
        loadUserDetails(userId);
      }
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      if (error.response?.status === 403) {
        setAdminPasswordEntered(false);
        localStorage.removeItem('admin_password');
        setPasswordError('Admin password required or invalid');
      }
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to unban user'
      });
    }
  };

  useEffect(() => {
    if (adminPasswordEntered && activeTab === 'messages') {
      loadMessages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messagesPage, activeTab, adminPasswordEntered]);

  return (
    <div className="admin-container">
      <div className="admin-window win98-bevel">
        <div className="navigation-bar win98-bevel-inset">
          <div className="nav-links">
            <Link to="/" className={`nav-link ${location.pathname === '/' ? 'active' : ''}`}>
              💬 Chat
            </Link>
            <Link to="/admin" className={`nav-link ${location.pathname === '/admin' ? 'active' : ''}`}>
              ⚙️ Admin
            </Link>
          </div>
        </div>

        <div className="admin-header">
          <h1 className="admin-title">🔧 Admin Panel</h1>
          <p className="admin-subtitle">Manage User Credits</p>
        </div>

        {!adminPasswordEntered ? (
          <div className="admin-content">
            <div className="admin-section win98-bevel-inset">
              <h2 className="section-title">Admin Password Required</h2>
              <form onSubmit={handleAdminPasswordSubmit} className="password-form">
                <div className="form-group">
                  <label>Admin Password:</label>
                  <input
                    type="password"
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    className="form-input win98-bevel-inset"
                    placeholder="Enter admin password"
                    required
                    autoFocus
                  />
                  {passwordError && (
                    <div className="password-error">{passwordError}</div>
                  )}
                </div>
                <button type="submit" className="win98-button primary-button">
                  Authenticate
                </button>
              </form>
              <p className="password-help">
                <strong>Note:</strong> If ADMIN_PASSWORD is not set in Railway, you can try submitting with an empty password.
                The admin panel will only work if your username matches ADMIN_USERNAME.
              </p>
            </div>
          </div>
        ) : (
          <>
            {message.text && (
              <div className={`admin-message ${message.type}`}>
                {message.text}
              </div>
            )}

            <div className="admin-content">
          {/* Tabs */}
          <div className="admin-tabs win98-bevel-inset">
            <button
              className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
              onClick={() => setActiveTab('users')}
            >
              👥 Users
            </button>
            <button
              className={`tab-button ${activeTab === 'messages' ? 'active' : ''}`}
              onClick={() => setActiveTab('messages')}
            >
              💬 Messages
            </button>
            <button
              className={`tab-button ${activeTab === 'payouts' ? 'active' : ''}`}
              onClick={() => setActiveTab('payouts')}
            >
              💰 Payouts
            </button>
          </div>

          {activeTab === 'users' && (
            <>
          {/* Bulk Actions */}
          <div className="admin-section win98-bevel-inset">
            <h2 className="section-title">Bulk Credit Addition</h2>
            <div className="bulk-actions">
              <div className="form-group">
                <label>Credit Amount:</label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={creditAmount}
                  onChange={(e) => setCreditAmount(e.target.value)}
                  className="form-input win98-bevel-inset"
                />
              </div>
              <div className="form-group">
                <label>Selected Users: {selectedUserIds.length}</label>
                <button
                  onClick={selectAll}
                  className="win98-button"
                >
                  {selectedUserIds.length === users.length ? 'Deselect All' : 'Select All on Page'}
                </button>
              </div>
              <button
                onClick={handleBulkAddCredits}
                className="win98-button primary-button"
                disabled={selectedUserIds.length === 0}
              >
                Add {creditAmount} Credits to {selectedUserIds.length} User{selectedUserIds.length !== 1 ? 's' : ''}
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="admin-section win98-bevel-inset">
            <div className="search-bar">
              <input
                type="text"
                placeholder="Search by username..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                className="form-input win98-bevel-inset"
              />
            </div>
          </div>

          {/* Users List */}
          <div className="admin-section win98-bevel-inset">
            <h2 className="section-title">Users ({total} total)</h2>
            {loading ? (
              <div className="loading">Loading users...</div>
            ) : (
              <>
                <div className="users-table">
                  <div className="table-header">
                    <div className="col-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedUserIds.length === users.length && users.length > 0}
                        onChange={selectAll}
                      />
                    </div>
                    <div className="col-username">Username</div>
                    <div className="col-credits">Credits</div>
                    <div className="col-wins">Wins</div>
                    <div className="col-status">Status</div>
                    <div className="col-joined">Joined</div>
                    <div className="col-actions">Actions</div>
                  </div>
                  {users.map(user => (
                    <div key={user.id} className={`table-row ${user.is_banned ? 'banned-user' : ''}`}>
                      <div className="col-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedUserIds.includes(user.id)}
                          onChange={() => toggleUserSelection(user.id)}
                          disabled={user.is_banned}
                        />
                      </div>
                      <div className="col-username">
                        {user.username}
                        {user.is_banned && <span className="banned-badge">🚫 BANNED</span>}
                        {user.warning_count > 0 && <span className="warning-badge">⚠️ {user.warning_count}</span>}
                      </div>
                      <div className="col-credits">💰 {parseFloat(user.credits || 0).toFixed(2)}</div>
                      <div className="col-wins">🏆 {user.wins || 0}</div>
                      <div className="col-status">
                        {user.is_banned ? (
                          <span className="status-banned">Banned</span>
                        ) : (
                          <span className="status-active">Active</span>
                        )}
                      </div>
                      <div className="col-joined">
                        {new Date(user.created_at).toLocaleDateString()}
                      </div>
                      <div className="col-actions">
                        <button
                          onClick={() => handleAddCredits(user.id, creditAmount)}
                          className="win98-button small-button"
                          title={`Add ${creditAmount} credits`}
                          disabled={user.is_banned}
                        >
                          +{creditAmount}
                        </button>
                        <button
                          onClick={() => setShowWarnModal(user)}
                          className="win98-button small-button warn-button"
                          disabled={user.is_banned}
                        >
                          ⚠️ Warn
                        </button>
                        {user.is_banned ? (
                          <button
                            onClick={() => handleUnban(user.id)}
                            className="win98-button small-button unban-button"
                          >
                            ✅ Unban
                          </button>
                        ) : (
                          <button
                            onClick={() => setShowBanModal(user)}
                            className="win98-button small-button ban-button"
                          >
                            🚫 Ban
                          </button>
                        )}
                        <button
                          onClick={() => loadUserDetails(user.id)}
                          className="win98-button small-button"
                        >
                          Details
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                <div className="pagination">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="win98-button"
                  >
                    ← Previous
                  </button>
                  <span>Page {page} of {Math.ceil(total / 50)}</span>
                  <button
                    onClick={() => setPage(p => p + 1)}
                    disabled={page >= Math.ceil(total / 50)}
                    className="win98-button"
                  >
                    Next →
                  </button>
                </div>
              </>
            )}
          </div>

          {/* User Details */}
          {selectedUser && (
            <div className="admin-section win98-bevel-inset">
              <h2 className="section-title">User Details: {selectedUser.user.username}</h2>
              <div className="user-details">
                <div className="detail-item">
                  <span className="detail-label">ID:</span>
                  <span className="detail-value">{selectedUser.user.id}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Credits:</span>
                  <span className="detail-value">💰 {parseFloat(selectedUser.user.credits || 0).toFixed(2)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Wins:</span>
                  <span className="detail-value">🏆 {selectedUser.user.wins || 0}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Total Messages:</span>
                  <span className="detail-value">{selectedUser.stats.total_messages}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Upvotes Received:</span>
                  <span className="detail-value">⬆️ {selectedUser.stats.total_upvotes_received}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Total Deposited:</span>
                  <span className="detail-value">💎 {selectedUser.stats.total_deposited.toFixed(2)} DOGE</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Joined:</span>
                  <span className="detail-value">{new Date(selectedUser.user.created_at).toLocaleString()}</span>
                </div>
                <div className="detail-actions">
                  <button
                    onClick={() => handleAddCredits(selectedUser.user.id, creditAmount)}
                    className="win98-button primary-button"
                  >
                    Add {creditAmount} Credits
                  </button>
                  <button
                    onClick={() => setSelectedUser(null)}
                    className="win98-button"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
          </>
          )}

          {activeTab === 'messages' && (
            <div className="admin-section win98-bevel-inset">
              <h2 className="section-title">Messages ({messagesTotal} total)</h2>
              {messagesLoading ? (
                <div className="loading">Loading messages...</div>
              ) : (
                <>
                  <div className="messages-table">
                    <div className="table-header">
                      <div className="col-message-id">ID</div>
                      <div className="col-message-user">User</div>
                      <div className="col-message-content">Content</div>
                      <div className="col-message-upvotes">Upvotes</div>
                      <div className="col-message-date">Date</div>
                      <div className="col-message-status">Status</div>
                      <div className="col-message-actions">Actions</div>
                    </div>
                    {messages.map(msg => (
                      <div key={msg.id} className={`table-row ${msg.is_deleted ? 'deleted-message' : ''}`}>
                        <div className="col-message-id">{msg.id}</div>
                        <div className="col-message-user">{msg.username}</div>
                        <div className="col-message-content">
                          {msg.is_deleted ? (
                            <span className="deleted-text">[DELETED] {msg.delete_reason}</span>
                          ) : (
                            msg.content.substring(0, 100) + (msg.content.length > 100 ? '...' : '')
                          )}
                        </div>
                        <div className="col-message-upvotes">⬆️ {msg.upvote_count}</div>
                        <div className="col-message-date">
                          {new Date(msg.created_at).toLocaleDateString()}
                        </div>
                        <div className="col-message-status">
                          {msg.is_deleted ? (
                            <span className="status-deleted">Deleted</span>
                          ) : (
                            <span className="status-active">Active</span>
                          )}
                        </div>
                        <div className="col-message-actions">
                          {!msg.is_deleted && (
                            <button
                              onClick={() => handleDeleteMessage(msg.id)}
                              className="win98-button small-button delete-button"
                            >
                              🗑️ Delete
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="pagination">
                    <button
                      onClick={() => setMessagesPage(p => Math.max(1, p - 1))}
                      disabled={messagesPage === 1}
                      className="win98-button"
                    >
                      ← Previous
                    </button>
                    <span>Page {messagesPage} of {Math.ceil(messagesTotal / 50)}</span>
                    <button
                      onClick={() => setMessagesPage(p => p + 1)}
                      disabled={messagesPage >= Math.ceil(messagesTotal / 50)}
                      className="win98-button"
                    >
                      Next →
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'payouts' && (
            <div className="admin-section win98-bevel-inset">
              <h2 className="section-title">💰 Pending Payout Verifications</h2>
              <p className="section-description" style={{ marginBottom: '15px', fontSize: '0.9rem', color: '#666' }}>
                Payouts that require 2FA verification. Check your email for verification codes from NOWPayments.
              </p>
              {payoutsLoading ? (
                <div className="loading">Loading pending payouts...</div>
              ) : pendingPayouts.length === 0 ? (
                <div className="empty-state" style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                  ✅ No pending payouts! All payouts have been verified.
                </div>
              ) : (
                <div className="payouts-table">
                  <div className="table-header">
                    <div className="col-payout-batch">Batch ID</div>
                    <div className="col-payout-user">User</div>
                    <div className="col-payout-rank">Rank</div>
                    <div className="col-payout-amount">Amount</div>
                    <div className="col-payout-address">Address</div>
                    <div className="col-payout-date">Date</div>
                    <div className="col-payout-verify">Verify</div>
                  </div>
                  {pendingPayouts.map(payout => (
                    <div key={payout.id} className="table-row">
                      <div className="col-payout-batch">
                        <code style={{ fontSize: '0.85rem' }}>{payout.batchId}</code>
                      </div>
                      <div className="col-payout-user">{payout.username}</div>
                      <div className="col-payout-rank">
                        {payout.rank === 1 ? '🥇' : payout.rank === 2 ? '🥈' : '🥉'} {payout.rank}
                      </div>
                      <div className="col-payout-amount">{parseFloat(payout.amount).toFixed(8)} DOGE</div>
                      <div className="col-payout-address">
                        <code style={{ fontSize: '0.8rem' }}>
                          {payout.payoutAddress.substring(0, 10)}...{payout.payoutAddress.substring(payout.payoutAddress.length - 8)}
                        </code>
                      </div>
                      <div className="col-payout-date">
                        <div style={{ fontSize: '0.85rem' }}>
                          {new Date(payout.createdAt).toLocaleDateString()}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '2px' }}>
                          {new Date(payout.createdAt).toLocaleTimeString()}
                        </div>
                      </div>
                      <div className="col-payout-verify">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                          <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                            <input
                              type="text"
                              placeholder="Code"
                              value={verificationCodes[payout.batchId] || ''}
                              onChange={(e) => setVerificationCodes(prev => ({
                                ...prev,
                                [payout.batchId]: e.target.value
                              }))}
                              className="form-input win98-bevel-inset"
                              style={{ width: '80px', padding: '4px 8px' }}
                              maxLength={10}
                            />
                            <button
                              onClick={() => handleVerifyPayout(payout.batchId)}
                              disabled={verifying[payout.batchId] || !verificationCodes[payout.batchId]}
                              className="win98-button small-button primary-button"
                              style={{ minWidth: '70px' }}
                            >
                              {verifying[payout.batchId] ? '...' : 'Verify'}
                            </button>
                          </div>
                          <button
                            onClick={() => handleSyncStatus(payout.batchId)}
                            disabled={syncing[payout.batchId]}
                            className="win98-button small-button"
                            style={{ fontSize: '0.75rem', padding: '4px 8px' }}
                            title="Check if payout was verified in NOWPayments dashboard"
                          >
                            {syncing[payout.batchId] ? 'Syncing...' : '🔄 Sync Status'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ marginTop: '15px' }}>
                <button
                  onClick={loadPendingPayouts}
                  className="win98-button"
                  disabled={payoutsLoading}
                >
                  🔄 Refresh
                </button>
              </div>
            </div>
          )}

          {/* Warn Modal */}
          {showWarnModal && (
            <Win98Dialog
              title={`Issue Warning to ${showWarnModal.username}`}
              icon="⚠️"
              onClose={() => {
                setShowWarnModal(null);
                setWarnReason('');
              }}
              width={500}
              buttons={[
                {
                  label: 'Cancel',
                  onClick: () => {
                    setShowWarnModal(null);
                    setWarnReason('');
                  }
                },
                {
                  label: 'Issue Warning',
                  onClick: handleWarn,
                  style: { background: '#d4d0c8' }
                }
              ]}
            >
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Reason:</label>
                <textarea
                  value={warnReason}
                  onChange={(e) => setWarnReason(e.target.value)}
                  className="form-input win98-bevel-inset"
                  placeholder="Enter warning reason..."
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '8px',
                    fontFamily: 'inherit',
                    fontSize: '12px',
                    resize: 'vertical'
                  }}
                />
              </div>
            </Win98Dialog>
          )}

          {/* Ban Modal */}
          {showBanModal && (
            <Win98Dialog
              title={`Ban User: ${showBanModal.username}`}
              icon="🚫"
              onClose={() => {
                setShowBanModal(null);
                setBanReason('');
              }}
              width={500}
              buttons={[
                {
                  label: 'Cancel',
                  onClick: () => {
                    setShowBanModal(null);
                    setBanReason('');
                  }
                },
                {
                  label: 'Ban User',
                  onClick: handleBan,
                  style: { background: '#ffcccc' }
                }
              ]}
            >
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Reason:</label>
                <textarea
                  value={banReason}
                  onChange={(e) => setBanReason(e.target.value)}
                  className="form-input win98-bevel-inset"
                  placeholder="Enter ban reason..."
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '8px',
                    fontFamily: 'inherit',
                    fontSize: '12px',
                    resize: 'vertical'
                  }}
                />
              </div>
            </Win98Dialog>
          )}
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;

